import { createFileRoute } from "@tanstack/react-router";
import { CalendarDays, Coffee, Compass, Crown, MessageCircleQuestion, Orbit, ShoppingBag, Stethoscope } from "lucide-react";
import { getCommunityPageData } from "@/lib/community.functions";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { GuestInviteBlock } from "@/components/GuestInviteBlock";
import { setRedirectIntent } from "@/lib/redirect-intent";
import { pluralWord } from "@/lib/plural";

const SITE = "https://turkey-drive.com";
const OG_IMAGE = `${SITE}/assets/social-preview.jpg`;
const TITLE = "Сообщество русскоязычных в Турции — TurkeyDrive";
const DESCRIPTION =
  "Сообщество русскоязычных в Турции: единая афиша событий, тематические сообщества, знакомства по интересам и эксперты на русском. Регистрация бесплатная.";

export const Route = createFileRoute("/community")({
  loader: () => getCommunityPageData(),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ru_RU" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/community` },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: `${SITE}/community` }],
  }),
  component: CommunityPage,
  errorComponent: ({ error }) => (
    <div role="alert" className="p-6 text-center text-sm text-td-muted">
      {error instanceof Error ? error.message : String(error)}
    </div>
  ),
});

function goToAuth(mode: "signup" | "signin") {
  setRedirectIntent("/home");
  window.location.assign(`/auth?redirect=${encodeURIComponent("/home")}&mode=${mode}`);
}

const INSIDE = [
  { Icon: CalendarDays, text: "Единая афиша событий по всей Турции" },
  { Icon: Orbit, text: "Тематические сообщества по интересам, по всей стране" },
  { Icon: Coffee, text: "Знакомства по интересам и кофе" },
  { Icon: Stethoscope, text: "Каталог экспертов: врачи, юристы, мастера на русском" },
  { Icon: Crown, text: "Консьерж-сервис: подберём вручную то, что сложно найти самому" },
  { Icon: MessageCircleQuestion, text: "«Спроси своих»: вопросы и ответы участников" },
  { Icon: ShoppingBag, text: "Работа, подрядчики и барахолка" },
];

function CommunityPage() {
  const { counts, expertCategories, circles } = Route.useLoaderData();

  const stats = [
    { n: counts.people, label: pluralWord(counts.people, "резидент", "резидента", "резидентов") },
    { n: counts.circles, label: pluralWord(counts.circles, "сообщество", "сообщества", "сообществ") },
    { n: counts.experts, label: pluralWord(counts.experts, "эксперт", "эксперта", "экспертов") },
  ].filter((s) => s.n > 0);

  return (
    <main className="min-h-screen overflow-x-hidden bg-white">
      <SiteHeader />

      {/* Блок 1: первый экран */}
      <section className="mx-auto w-full max-w-[720px] px-4 pt-10 text-center">
        <h1 className="font-brand text-3xl font-bold leading-tight text-td-dark md:text-4xl">
          Сообщество русскоязычных в Турции
        </h1>
        <p className="mx-auto mt-3 max-w-[520px] text-[16px] leading-snug text-td-muted">
          Афиша событий, знакомства по интересам, эксперты на русском языке — в одном месте.
        </p>
        <div className="mt-6 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => goToAuth("signup")}
            className="flex h-[52px] w-full max-w-[340px] items-center justify-center rounded-[18px] bg-td-orange text-[17px] font-bold text-white shadow-[0_14px_30px_-12px_rgba(229,114,42,.7)] transition hover:bg-td-orange/90"
          >
            Присоединиться бесплатно
          </button>
          <a
            href="/events"
            className="flex min-h-11 items-center text-[15px] font-semibold text-td-teal hover:underline"
          >
            Смотреть афишу →
          </a>
        </div>
      </section>

      {/* Блок 2: живые цифры */}
      {stats.length > 0 && (
        <section className="mx-auto mt-10 w-full max-w-[720px] px-4">
          <div className="rounded-[22px] p-5" style={{ backgroundColor: "#0D4A47" }}>
            <p className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.12em]" style={{ color: "#7FE0DA" }}>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-td-teal opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-td-teal" />
              </span>
              Сейчас в TurkeyDrive
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {stats.map(({ n, label }) => (
                <div key={label} className="min-w-0">
                  <p className="font-display text-[28px] font-bold leading-none text-white md:text-[36px]">{n}</p>
                  <p className="mt-1 text-[12px] leading-tight md:text-[14px]" style={{ color: "#CFEDEA" }}>{label}</p>
                </div>
              ))}
            </div>
            {expertCategories.length > 0 && (
              <p className="mt-4 text-[13px] leading-snug" style={{ color: "#CFEDEA" }}>
                Эксперты по категориям:{" "}
                {expertCategories.map((c) => `${c.category} — ${c.n}`).join(", ")}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Блок 3: что внутри */}
      <section className="mx-auto mt-10 w-full max-w-[720px] px-4">
        <h2 className="font-brand text-xl font-bold text-td-dark">Что внутри</h2>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {INSIDE.map(({ Icon, text }) => (
            <div key={text} className="flex items-center gap-2.5 rounded-[18px] bg-surface-25 p-3.5">
              <Icon className="h-5 w-5 shrink-0 text-td-teal" aria-hidden="true" />
              <p className="min-w-0 text-[14px] font-semibold leading-tight text-td-dark">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Блок 4: как работает подбор людей */}
      <section className="mx-auto mt-10 w-full max-w-[720px] px-4">
        <h2 className="font-brand text-xl font-bold text-td-dark">Как работает подбор людей</h2>
        <p className="mt-2 text-[15px] leading-relaxed text-td-dark">
          Ты заполняешь карточку: город, интересы, чем занимаешься. Система находит людей с похожими
          интересами, в том числе в твоём городе. Знакомиться или нет, решаешь сам.
        </p>
        <div className="mt-3 rounded-[18px] border border-dashed border-td-line bg-surface-25 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-td-muted">Пример</p>
          <p className="mt-1 text-[14px] leading-snug text-td-dark">
            Анна, Анталья. Интересы: йога, дети, пешие прогулки. Подборка: 3 человека с похожими интересами.
          </p>
        </div>
      </section>

      {/* Блок 5: тематические сообщества */}
      {circles.length > 0 && (
        <section className="mx-auto mt-10 w-full max-w-[720px] px-4">
          <h2 className="font-brand text-xl font-bold text-td-dark">Тематические сообщества</h2>
          <ul className="mt-3 flex flex-col gap-2">
            {circles.map((c) => (
              <li key={c.id} className="rounded-[18px] bg-surface-25 px-4 py-3">
                <p className="text-[15px] font-semibold leading-tight text-td-dark">{c.name}</p>
                {c.topics.length > 0 && (
                  <p className="mt-0.5 text-[13px] text-td-muted">{c.topics.join(" · ")}</p>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[13px] text-td-muted">Все сообщества — после регистрации.</p>
        </section>
      )}

      {/* Блок 6: эксперты */}
      <section className="mx-auto mt-10 w-full max-w-[720px] px-4">
        <h2 className="font-brand text-xl font-bold text-td-dark">Эксперты</h2>
        <p className="mt-2 text-[15px] leading-relaxed text-td-dark">
          Врачи, юристы, мастера, которые говорят на русском. Контакты и карточки видны после регистрации.
        </p>
      </section>

      {/* Блок 7: как начать */}
      <section className="mx-auto mt-10 w-full max-w-[720px] px-4">
        <h2 className="font-brand text-xl font-bold text-td-dark">Как начать</h2>
        <ol className="mt-3 flex flex-col gap-2">
          {[
            "Зарегистрируйся — займёт минуту.",
            "Заполни карточку: город и интересы.",
            "Выбери сообщества и открой афишу.",
          ].map((step, i) => (
            <li key={step} className="flex items-start gap-3 rounded-[18px] bg-surface-25 p-3.5">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-td-teal text-[14px] font-bold text-white">
                {i + 1}
              </span>
              <p className="min-w-0 pt-0.5 text-[15px] leading-snug text-td-dark">{step}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Блок 8: вопросы и ответы (платных шагов при регистрации нет — подтверждено в коде) */}
      <section className="mx-auto mt-10 w-full max-w-[720px] px-4">
        <h2 className="font-brand text-xl font-bold text-td-dark">Вопросы и ответы</h2>
        <div className="mt-3">
          <h3 className="text-[15px] font-semibold text-td-dark">Это бесплатно?</h3>
          <p className="mt-1 text-[14px] text-td-muted">Да, регистрация бесплатная.</p>
        </div>
      </section>

      {/* Блок 9: финальный призыв */}
      <section className="mx-auto mt-12 w-full max-w-[720px] px-4 pb-12">
        <div className="text-center">
          <p className="font-brand text-2xl font-bold leading-tight text-td-dark">
            Почувствуй себя в Турции как дома.
          </p>
          <button
            type="button"
            onClick={() => goToAuth("signup")}
            className="mx-auto mt-4 flex h-[52px] w-full max-w-[340px] items-center justify-center rounded-[18px] bg-td-orange text-[17px] font-bold text-white shadow-[0_14px_30px_-12px_rgba(229,114,42,.7)] transition hover:bg-td-orange/90"
          >
            Присоединиться бесплатно
          </button>
          <button
            type="button"
            onClick={() => goToAuth("signin")}
            className="mx-auto mt-1 flex min-h-11 items-center text-[14px] text-td-muted"
          >
            Уже с нами? <span className="ml-1 font-semibold text-td-teal underline">Войти</span>
          </button>
        </div>
        <GuestInviteBlock className="mt-8" onSignup={() => goToAuth("signup")} onLogin={() => goToAuth("signin")} />
        <p className="mt-6 text-center text-[13px] text-td-muted">
          <a href="/" className="hover:text-td-dark">← На главную</a>
        </p>
      </section>
    </main>
  );
}
